import React from "react";
import Shell from "@/pages/LegalShell";

const H2 = ({ children }) => (
  <h2 className="font-heading text-2xl font-bold text-white mt-12 mb-2 tracking-tight">
    {children}
  </h2>
);

const H3 = ({ children }) => (
  <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 mt-6 mb-1">
    {children}
  </h3>
);

const UL = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-slate-300">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

const PURPOSES = [
  ["To operate, administer, and improve the Site", "Identity, Contact, Technical, Usage", "Legitimate interests (running and improving our business and Site)"],
  ["To respond to enquiries, requests, or correspondence", "Identity, Contact, User Submitted", "Legitimate interests; steps prior to contract"],
  ["To provide products or services and manage customer relationships", "Identity, Contact, Transaction & Service, User Submitted", "Performance of a contract; legitimate interests"],
  ["To process payments, invoices, and records", "Identity, Contact, Transaction & Service", "Performance of a contract; legal obligation; legitimate interests"],
  ["To send service-related communications", "Identity, Contact, Transaction & Service", "Performance of a contract; legitimate interests"],
  ["To send marketing communications where permitted", "Identity, Contact, Marketing & Communications, Usage", "Consent where required; otherwise legitimate interests"],
  ["To personalise content and measure campaign effectiveness", "Technical, Usage, Marketing & Communications", "Consent where required; otherwise legitimate interests"],
  ["To maintain security, monitor misuse, and prevent fraud", "Identity, Contact, Technical, Usage, Transaction & Service", "Legitimate interests; legal obligation"],
  ["To comply with legal, regulatory, and compliance obligations", "Any relevant category", "Legal obligation; legitimate interests"],
  ["To establish, exercise, or defend legal claims", "Any relevant category", "Legitimate interests; legal claims"],
];

const Privacy = () => (
  <Shell
    title="Privacy Policy"
    kicker="Your data. Your business."
    testid="privacy-page"
    path="/privacy"
  >
    <p className="text-slate-400">
      Last updated: 4 June 2026
    </p>

    <p>
      This Privacy Policy explains how Paisley on the web, a
      company incorporated in the United Kingdom with registered number{" "}
      891394 and registered office at{" "}
      Unit 167603, PO Box 7169, Poole, BH15 9EL (we, us, or our), collects, uses,
      stores, shares, and otherwise processes personal data in connection with{" "}
      <a className="text-cyan-300 hover:text-white" href="https://clydeflow.com">
        https://clydeflow.com
      </a>{" "}
      and any related webpages, services, communications, and interactions
      (collectively, the Site).
    </p>
    <p>
      We are the controller of personal data processed under this Privacy
      Policy, unless we state otherwise.
    </p>

    <div className="mt-8 rounded-sm border border-cyan-400/30 bg-cyan-400/[0.04] p-5" data-testid="privacy-connected-platforms">
      <h2 className="font-heading text-xl font-bold text-white mb-3 tracking-tight">
        Connected Platforms &amp; Data Deletion
      </h2>
      <p className="mb-3">
        ClydeFlow lets you connect third-party accounts — <strong>TikTok</strong>,{" "}
        <strong>Meta</strong> (Instagram and Facebook), and <strong>Google</strong> — so the
        Site can perform actions you request, such as publishing content and
        managing messages. We only access the data permitted by the specific
        permissions (scopes) you grant when you connect, and we use it solely to
        provide those features. We never sell this data or use it for advertising.
      </p>
      <p className="mb-2 font-semibold text-white">TikTok</p>
      <p className="mb-3">
        When you connect TikTok we request the following scopes and use the data
        as described:
      </p>
      <ul className="list-disc pl-6 space-y-1.5 mb-3">
        <li><code className="text-cyan-300">user.info.profile</code> — to display your TikTok profile details (username, profile link, bio, verified status) so you can confirm which account is connected.</li>
        <li><code className="text-cyan-300">user.info.stats</code> — to display your account statistics (follower, following, likes, and video counts) so you can track your growth.</li>
        <li><code className="text-cyan-300">video.list</code> — to display your public TikTok videos within the app so you can review and manage them.</li>
      </ul>
      <p className="mb-3">
        We handle TikTok data in accordance with the{" "}
        <a className="text-cyan-300 hover:text-white" href="https://developers.tiktok.com/doc/tiktok-api-platform-terms-of-service" target="_blank" rel="noopener noreferrer">TikTok Developer Terms</a>{" "}
        and Community Guidelines. Access tokens are stored securely and used only
        to perform the actions you authorise.
      </p>
      <p className="mb-2 font-semibold text-white">Disconnecting &amp; revoking access</p>
      <p className="mb-3">
        You can disconnect any platform at any time from your ClydeFlow
        dashboard, which revokes our ongoing access. You can also revoke access
        directly in the platform’s own settings (for TikTok: Settings → Security
        &amp; permissions → Manage app permissions).
      </p>
      <p className="mb-2 font-semibold text-white">How to delete your data</p>
      <p>
        To request deletion of the data we hold from a connected platform, or of
        your entire ClydeFlow account, disconnect the platform and email us at{" "}
        <a className="text-cyan-300 hover:text-white" href="mailto:privacy@clydeflow.com">privacy@clydeflow.com</a>.
        We will delete the associated personal data within 30 days, except where
        we are required to retain it to meet a legal obligation.
      </p>
    </div>

    <H2>1. Important Information</H2>
    <p>This Privacy Policy applies to personal data we collect from:</p>
    <UL
      items={[
        "visitors to the Site;",
        "prospective customers, customers, and their personnel;",
        "individuals who contact us, request information, or subscribe to updates;",
        "individuals who interact with us on social media or through marketing communications; and",
        "any other individuals whose personal data we process in the course of operating the Site and our business.",
      ]}
    />
    <p>
      This Site is not intended for children, and we do not knowingly collect
      personal data from children.
    </p>

    <H2>2. The Personal Data We Collect</H2>
    <p>
      We may collect, use, store, and transfer different categories of personal
      data, including:
    </p>
    <H3>2.1 Identity Data</H3>
    <p>
      Name, title, username or similar identifier, job title, employer, and
      other identifying information.
    </p>
    <H3>2.2 Contact Data</H3>
    <p>
      Billing address, correspondence address, email address, telephone number,
      and other contact details.
    </p>
    <H3>2.3 Technical Data</H3>
    <p>
      Internet protocol (IP) address, browser type and version, device
      identifiers, time zone setting, browser plug-in types and versions,
      operating system, platform, and other technology on the devices you use to
      access the Site.
    </p>
    <H3>2.4 Usage Data</H3>
    <p>
      Information about how you use the Site, products, and services, including
      page views, navigation paths, referral sources, access times, and
      interactions with content.
    </p>
    <H3>2.5 Marketing and Communications Data</H3>
    <p>
      Your preferences in receiving marketing from us and our third parties, your
      communication preferences, and records of correspondence.
    </p>
    <H3>2.6 Transaction and Service Data</H3>
    <p>
      Details about services you enquire about or purchase, contract
      information, payment-related information, invoicing details, and records
      relating to service delivery and customer support.
    </p>
    <H3>2.7 User Submitted Data</H3>
    <p>
      Any personal data included in forms, messages, uploads, support requests,
      survey responses, feedback, or other content you submit through the Site.
    </p>
    <p>
      We may also collect, use, and share aggregated or anonymised data that does
      not identify you. If we combine aggregated data with personal data so that
      you can be identified, we will treat it as personal data.
    </p>

    <H2>3. How We Collect Personal Data</H2>
    <p>We collect personal data:</p>
    <UL
      items={[
        "directly from you when you fill in forms, contact us, sign up for updates, request a demo, create an account, purchase services, or otherwise interact with us;",
        "automatically through cookies, server logs, pixels, analytics tools, and similar technologies when you use the Site;",
        "from third parties, such as analytics providers, advertising networks, payment processors, CRM providers, identity verification providers, social media platforms, publicly available sources, and business partners; and",
        "from your employer or colleagues where they provide your details in connection with a business relationship.",
      ]}
    />

    <H2>4. How We Use Personal Data and Our Lawful Bases</H2>
    <p>
      We will process your personal data only where we have a lawful basis to do
      so under applicable data protection law. Depending on the circumstances, we
      rely on one or more of the following lawful bases:
    </p>
    <UL
      items={[
        "performance of a contract or taking steps at your request before entering into a contract;",
        "compliance with a legal obligation;",
        "our legitimate interests, provided those interests are not overridden by your interests or fundamental rights and freedoms; and",
        "your consent, where consent is required by law.",
      ]}
    />
    <p>We may use your personal data for the following purposes:</p>
    <div className="overflow-x-auto mt-4 rounded-sm border border-white/10">
      <table className="w-full text-sm text-left border-collapse">
        <thead>
          <tr className="bg-white/5 text-cyan-300 font-mono text-[10px] uppercase tracking-wider">
            <th className="px-4 py-3 border-b border-white/10">Purpose</th>
            <th className="px-4 py-3 border-b border-white/10">Categories of Personal Data</th>
            <th className="px-4 py-3 border-b border-white/10">Lawful Basis</th>
          </tr>
        </thead>
        <tbody>
          {PURPOSES.map((row, i) => (
            <tr key={i} className="align-top">
              <td className="px-4 py-3 border-b border-white/5 text-white">{row[0]}</td>
              <td className="px-4 py-3 border-b border-white/5 text-slate-400">{row[1]}</td>
              <td className="px-4 py-3 border-b border-white/5 text-slate-400">{row[2]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="mt-4">
      If we rely on consent, you may withdraw it at any time, but this will not
      affect the lawfulness of processing carried out before withdrawal.
    </p>

    <H2>5. Marketing</H2>
    <p>
      We may send you marketing communications about our products, services,
      updates, or events where you have requested them, consented where required,
      or where we are otherwise permitted by law to do so.
    </p>
    <p>
      You can opt out of marketing communications at any time by using the
      unsubscribe link in an email or by contacting us at{" "}
      <a className="text-cyan-300 hover:text-white" href="mailto:privacy@clydeflow.com">
        privacy@clydeflow.com
      </a>
      .
    </p>
    <p>
      Opting out of marketing will not affect service-related communications
      necessary for an existing relationship or transaction.
    </p>

    <H2>6. Cookies and Similar Technologies</H2>
    <p>
      We may use cookies, pixels, tags, scripts, local storage, and similar
      technologies to operate the Site, remember preferences, analyse traffic,
      improve performance, and support marketing activities.
    </p>
    <p>
      Where required by law, we will request your consent before placing
      non-essential cookies or similar technologies on your device. You can
      manage your preferences through our cookie banner or browser settings.
    </p>
    <p>
      We do not currently publish a separate cookie notice; this Privacy Policy
      describes how we use cookies and similar technologies.
    </p>

    <H2>7. Disclosures of Personal Data</H2>
    <p>
      We may share personal data with the following categories of recipients
      where reasonably necessary:
    </p>
    <UL
      items={[
        "our affiliates and group companies;",
        "hosting providers, cloud service providers, website developers, analytics providers, CRM providers, communication platforms, payment processors, customer support providers, and other IT or operational service providers;",
        "professional advisers, including lawyers, accountants, insurers, auditors, and consultants;",
        "regulators, law enforcement agencies, courts, tribunals, government authorities, and other third parties where disclosure is required by law or necessary to protect our rights;",
        "prospective buyers, sellers, investors, or acquirers in connection with a merger, acquisition, financing, reorganisation, or sale of assets; and",
        "any other person with your consent or at your direction.",
      ]}
    />
    <p>
      We require processors acting on our behalf to process personal data only on
      our instructions, to keep it secure, and to use it only for authorised
      purposes.
    </p>

    <H2>8. International Transfers</H2>
    <p>
      We may transfer personal data to countries outside the United Kingdom or
      the European Economic Area where our service providers or business partners
      operate.
    </p>
    <p>
      Where we transfer personal data internationally, we will take appropriate
      safeguards as required by applicable law, which may include:
    </p>
    <UL
      items={[
        "adequacy regulations or adequacy decisions;",
        "the UK International Data Transfer Agreement, the UK Addendum to the EU Standard Contractual Clauses, or the European Commission’s Standard Contractual Clauses; or",
        "other valid transfer mechanisms recognised by law.",
      ]}
    />
    <p>You may contact us for more information about the safeguards we use.</p>

    <H2>9. Data Security</H2>
    <p>
      We use appropriate technical and organisational measures designed to
      protect personal data against accidental or unlawful destruction, loss,
      alteration, unauthorised disclosure, or access.
    </p>
    <p>
      However, no method of transmission over the internet or method of
      electronic storage is completely secure, and we cannot guarantee absolute
      security.
    </p>

    <H2>10. Data Retention</H2>
    <p>
      We will retain personal data only for as long as reasonably necessary for
      the purposes for which it was collected, including to satisfy legal,
      regulatory, tax, accounting, reporting, and enforcement requirements.
    </p>
    <p>
      To determine the appropriate retention period, we consider the amount,
      nature, and sensitivity of the personal data, the potential risk of harm
      from unauthorised use or disclosure, the purposes for which we process it,
      whether we can achieve those purposes through other means, and applicable
      legal requirements.
    </p>
    <p>As a general guide:</p>
    <UL
      items={[
        "enquiry data is retained for as long as needed to handle your enquiry and for a reasonable period afterwards;",
        "customer and contract records are retained for the duration of the relationship and for a reasonable period afterwards to meet legal, tax, and accounting requirements;",
        "marketing suppression records may be retained for as long as necessary to maintain opt-out preferences; and",
        "technical logs and analytics data are retained in line with operational and security needs.",
      ]}
    />

    <H2>11. Your Legal Rights</H2>
    <p>
      Depending on your location and the applicable law, you may have rights to:
    </p>
    <UL
      items={[
        "request access to your personal data;",
        "request correction of inaccurate or incomplete personal data;",
        "request erasure of your personal data;",
        "request restriction of processing;",
        "object to processing based on legitimate interests or direct marketing;",
        "request transfer of your personal data to you or a third party;",
        "withdraw consent where processing is based on consent; and",
        "complain to a supervisory authority.",
      ]}
    />
    <p>
      In the UK, the supervisory authority is the Information Commissioner’s
      Office (ICO), which can be contacted through{" "}
      <a className="text-cyan-300 hover:text-white" href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer">
        www.ico.org.uk
      </a>
      .
    </p>
    <p>
      We may need to request specific information from you to confirm your
      identity before responding to a request. We may also refuse or limit a
      request where permitted by law.
    </p>

    <H2>12. Third-Party Websites</H2>
    <p>
      The Site may include links to third-party websites, plug-ins, or
      applications. Clicking on those links or enabling those connections may
      allow third parties to collect or share data about you. We do not control
      third-party websites and are not responsible for their privacy statements
      or practices.
    </p>

    <H2>13. Changes to This Privacy Policy</H2>
    <p>
      We may update this Privacy Policy from time to time. Any changes will be
      posted on this page and, where appropriate, notified to you by other means.
      Please check back periodically to stay informed.
    </p>

    <H2>14. Contact Us</H2>
    <p>
      If you have questions about this Privacy Policy or our privacy practices, or
      if you wish to exercise your rights, please contact:
    </p>
    <div className="text-slate-300 leading-relaxed">
      Paisley on the web (Brian McGuire)
      <br />
      Unit 167603, PO Box 7169, Poole, BH15 9EL
      <br />
      Email:{" "}
      <a className="text-cyan-300 hover:text-white" href="mailto:privacy@clydeflow.com">
        privacy@clydeflow.com
      </a>
    </div>

    <H2>15. Representative and Data Protection Officer</H2>
    <p>
      If required under applicable law, our UK representative, EU representative,
      or Data Protection Officer can be contacted at:
    </p>
    <p>
      We have not appointed a Data Protection Officer or an external
      representative. For any data protection queries, please use the contact
      details in section 14 above.
    </p>
  </Shell>
);

export default Privacy;
